import { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import VN from "../components/VN";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import axios from "axios";
import { Row, Col, Container } from "react-bootstrap";

import {
  Select,
  TextField,
  Button,
  Typography,
  Box,
  InputLabel,
  MenuItem,
  FormControl,
  CircularProgress,
  FormHelperText,
  InputAdornment,
  Checkbox,
  FormControlLabel,
  Paper,
  Divider,
  Stack,
  Alert,
  LinearProgress,
} from "@mui/material";

import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import SmsOutlinedIcon from "@mui/icons-material/SmsOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

import { apiRequest } from "../components/Config";
import ReCAPTCHA from "react-google-recaptcha";
import { UserDataContext } from "../components/UserDataContext";
import LocationSelect from "../components/LocationSelect";
import PasswordFields from "../components/PasswordFields";
import { API_URL } from "../config/api";
import Alerts from "../components/Alerts";

export default function RegisterNanny() {
  const { userData } = useContext(UserDataContext);
  const navigate = useNavigate();

  const [step, setStep] = useState(1);

  const [phone, setPhone] = useState("");
  const [citizenshipList, setCitizenshipList] = useState([]);

  const [textEmail, setTextEmail] = useState("");
  const [showTextEmail, setShowTextEmail] = useState(false);

  const [textPhone, setTextPhone] = useState("");
  const [showTextPhone, setShowTextPhone] = useState(false);

  const [captchaToken, setCaptchaToken] = useState(null);

  const [email, setEmail] = useState("");

  const [apiResponse, setApiResponse] = useState({});
  const [showApiResponse, setShowApiResponse] = useState(false);

  const [countdown, setCountdown] = useState(5);
  const [showSubmit, setShowSubmit] = useState(true);

  /*
   * ============================================================
   * WALIDACJA
   * ============================================================
   */

  const validationSchema = Yup.object({
    email: Yup.string()
      .email("Podaj poprawny adres e-mail")
      .required("Adres e-mail jest wymagany"),

    phone: Yup.string()
      .required("Numer telefonu jest wymagany")
      .length(9, "Numer telefonu musi mieć 9 cyfr"),

    fullName: Yup.string().required("Imię i nazwisko są wymagane"),

    birthyear: Yup.number()
      .typeError("Wybierz rok urodzenia")
      .min(1960, "Niepoprawny rok")
      .max(new Date().getFullYear() - 28, "Opiekunka musi mieć minimum 28 lat")
      .required("Rok urodzenia jest wymagany"),

    password: Yup.string()
      .min(8, "Hasło musi mieć minimum 8 znaków")
      .matches(/[0-9]/, "Hasło musi zawierać co najmniej jedną cyfrę")
      .matches(
        /[^a-zA-Z0-9]/,
        "Hasło musi zawierać co najmniej jeden znak specjalny",
      )
      .required("Hasło jest wymagane"),

    passwordrepeat: Yup.string()
      .oneOf([Yup.ref("password")], "Hasła muszą być identyczne")
      .required("Powtórz hasło"),

    voivodeship: Yup.string().required("Wybierz województwo"),

    city: Yup.object().nullable().required("Wybierz miasto zamieszkania"),

    rodo: Yup.boolean().oneOf(
      [true],
      "Akceptacja polityki prywatności jest wymagana",
    ),

    citizenship: Yup.string().required("Obywatelstwo jest wymagane"),
  });

  /*
   * ============================================================
   * OBYWATELSTWA
   * ============================================================
   */

  useEffect(() => {
    apiRequest("citizenships")
      .then(setCitizenshipList)
      .catch((error) => {
        console.error("Błąd pobierania obywatelstw:", error);
      });
  }, []);

  /*
   * ============================================================
   * SPRAWDZENIE E-MAIL
   * ============================================================
   */

  useEffect(() => {
    if (!email || !email.includes("@")) {
      setShowTextEmail(false);
      return;
    }

    const timeout = setTimeout(async () => {
      const url = API_URL + "/verify-account-email";

      try {
        const response = await axios.post(
          url,
          { email },
          {
            headers: {
              "Content-Type": "application/json",
            },
          },
        );

        if (parseInt(response.data.email) > 0) {
          setTextEmail("Konto z tym adresem e-mail już istnieje.");
          setShowTextEmail(true);
          setShowSubmit(false);
        } else {
          setShowTextEmail(false);
          setShowSubmit(true);
        }
      } catch (error) {
        console.error(error.message);
      }
    }, 500);

    return () => clearTimeout(timeout);
  }, [email]);

  /*
   * ============================================================
   * SPRAWDZENIE TELEFONU
   * ============================================================
   */

  useEffect(() => {
    if (!phone || phone.length !== 9) {
      setShowTextPhone(false);
      return;
    }

    const timeout = setTimeout(async () => {
      const url = API_URL + "/verify-account-phone";

      try {
        const response = await axios.post(
          url,
          { phone },
          {
            headers: {
              "Content-Type": "application/json",
            },
          },
        );

        if (parseInt(response.data.phone) > 0) {
          setTextPhone("Konto z tym numerem telefonu już istnieje.");
          setShowTextPhone(true);
          setShowSubmit(false);
        } else {
          setShowTextPhone(false);
          setShowSubmit(true);
        }
      } catch (error) {
        console.error(error.message);
      }
    }, 500);

    return () => clearTimeout(timeout);
  }, [phone]);

  /*
   * ============================================================
   * WSPÓLNY STYL PÓL
   * ============================================================
   */

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 2,
      backgroundColor: "#fff",
    },
  };

  /*
   * ============================================================
   * NAGŁÓWEK SEKCJI
   * ============================================================
   */

  const SectionTitle = ({ icon, title, description }) => (
    <Box sx={{ mb: 2.5 }}>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: "50%",
            backgroundColor: "#edf7ed",
            color: "#2e7d32",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {icon}
        </Box>

        <Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              lineHeight: 1.2,
            }}
          >
            {title}
          </Typography>

          {description && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.4 }}>
              {description}
            </Typography>
          )}
        </Box>
      </Stack>
    </Box>
  );

  /*
   * ============================================================
   * WSKAŹNIK ETAPÓW
   * ============================================================
   */

  const RegistrationSteps = () => (
    <Box sx={{ mb: 4 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="center"
        spacing={1}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: "50%",
              bgcolor: "#2e7d32",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 700,
            }}
          >
            {step > 1 ? <CheckCircleOutlineIcon fontSize="small" /> : "1"}
          </Box>

          <Typography
            variant="body2"
            sx={{
              fontWeight: step === 1 ? 700 : 500,
            }}
          >
            Dane
          </Typography>
        </Stack>

        <Box
          sx={{
            width: {
              xs: 25,
              sm: 70,
            },
            height: 2,
            bgcolor: step >= 2 ? "#2e7d32" : "#ddd",
          }}
        />

        <Stack direction="row" spacing={1} alignItems="center">
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: "50%",
              bgcolor: step >= 2 ? "#2e7d32" : "#e0e0e0",
              color: step >= 2 ? "#fff" : "#777",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 700,
            }}
          >
            2
          </Box>

          <Typography
            variant="body2"
            sx={{
              fontWeight: step === 2 ? 700 : 500,
            }}
          >
            SMS
          </Typography>
        </Stack>

        <Box
          sx={{
            width: {
              xs: 25,
              sm: 70,
            },
            height: 2,
            bgcolor: "#ddd",
          }}
        />

        <Stack direction="row" spacing={1} alignItems="center">
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: "50%",
              bgcolor: "#e0e0e0",
              color: "#777",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 700,
            }}
          >
            3
          </Box>

          <Typography variant="body2">Gotowe</Typography>
        </Stack>
      </Stack>
    </Box>
  );

  /*
   * ============================================================
   * RENDER
   * ============================================================
   */

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: "#faf8f5",
        pb: 8,
      }}
    >
      <VN userData={userData} />

      {showApiResponse && (
        <Row className="sticky-alerts-container">
          <Alerts data={apiResponse} />
        </Row>
      )}

      <Container>
        <Box
          sx={{
            maxWidth: 950,
            mx: "auto",
            pt: {
              xs: 3,
              md: 6,
            },
          }}
        >
          {/* ===================================================
              NAGŁÓWEK
          =================================================== */}

          <Box
            sx={{
              textAlign: "center",
              mb: 4,
            }}
          >
            <Typography
              component="h1"
              sx={{
                fontSize: {
                  xs: "1.9rem",
                  md: "2.5rem",
                },
                fontWeight: 800,
                color: "#263238",
                mb: 1.5,
              }}
            >
              Załóż profil opiekunki
            </Typography>

            <Typography
              sx={{
                maxWidth: 650,
                mx: "auto",
                color: "text.secondary",
                fontSize: {
                  xs: "0.95rem",
                  md: "1.05rem",
                },
                lineHeight: 1.7,
              }}
            >
              Dołącz do TwojaOpiekunka.pl i daj się znaleźć osobom, które
              poszukują odpowiedzialnej i godnej zaufania opiekunki.
            </Typography>

            <Stack
              direction={{
                xs: "column",
                sm: "row",
              }}
              spacing={2}
              justifyContent="center"
              sx={{ mt: 2.5 }}
            >
              <Stack
                direction="row"
                spacing={0.7}
                alignItems="center"
                justifyContent="center"
              >
                <CheckCircleOutlineIcon
                  sx={{ color: "#2e7d32", fontSize: 19 }}
                />

                <Typography variant="body2">Bezpłatna rejestracja</Typography>
              </Stack>

              <Stack
                direction="row"
                spacing={0.7}
                alignItems="center"
                justifyContent="center"
              >
                <CheckCircleOutlineIcon
                  sx={{ color: "#2e7d32", fontSize: 19 }}
                />

                <Typography variant="body2">
                  Weryfikacja numeru telefonu
                </Typography>
              </Stack>

              <Stack
                direction="row"
                spacing={0.7}
                alignItems="center"
                justifyContent="center"
              >
                <CheckCircleOutlineIcon
                  sx={{ color: "#2e7d32", fontSize: 19 }}
                />

                <Typography variant="body2">
                  Profil widoczny dla rodzin
                </Typography>
              </Stack>
            </Stack>
          </Box>

          {/* ===================================================
              KARTA
          =================================================== */}

          <Paper
            elevation={0}
            sx={{
              borderRadius: {
                xs: 0,
                sm: 4,
              },
              border: {
                xs: "none",
                sm: "1px solid #e5e5e5",
              },
              p: {
                xs: 2,
                sm: 4,
                md: 5,
              },
              boxShadow: {
                xs: "none",
                sm: "0 12px 40px rgba(0,0,0,0.06)",
              },
            }}
          >
            <RegistrationSteps />

            {/* =================================================
                KROK 1
            ================================================= */}

            {step === 1 && (
              <Formik
                initialValues={{
                  user: "",
                  email: "",
                  phone: "",
                  fullName: "",
                  birthyear: "",
                  password: "",
                  passwordrepeat: "",
                  voivodeship: "",
                  citizenship: "",
                  city: null,
                  rodo: false,
                }}
                validationSchema={validationSchema}
                onSubmit={async (values, { setSubmitting }) => {
                  setShowApiResponse(false);

                  if (!captchaToken) {
                    setApiResponse({
                      title: "Potwierdź, że nie jesteś robotem",
                      severity: "warning",
                      info: "Zaznacz pole reCAPTCHA przed utworzeniem konta.",
                    });

                    setShowApiResponse(true);
                    setSubmitting(false);
                    return;
                  }

                  try {
                    const payload = {
                      ...values,
                      city: values.city.id,
                      captcha: captchaToken,
                      accountType: 1,
                    };

                    const response = await apiRequest(
                      "register",
                      "POST",
                      payload,
                    );

                    console.log("REGISTER RESPONSE:", response);

                    setPhone(values.phone);
                    setStep(2);

                    window.scrollTo({
                      top: 0,
                      behavior: "smooth",
                    });
                  } catch (error) {
                    console.error(error);

                    setApiResponse({
                      title: "Nie udało się utworzyć konta",
                      severity: "error",
                      info: "Wystąpił problem podczas rejestracji. Spróbuj ponownie.",
                    });

                    setShowApiResponse(true);
                  } finally {
                    setSubmitting(false);
                  }
                }}
              >
                {({
                  values,
                  handleChange,
                  setFieldValue,
                  errors,
                  touched,
                  isSubmitting,
                }) => (
                  <Form>
                    {/* =========================================
                        DANE KONTAKTOWE
                    ========================================= */}

                    <SectionTitle
                      icon={<PersonOutlineIcon />}
                      title="Dane kontaktowe"
                      description="Podaj podstawowe informacje potrzebne do utworzenia konta."
                    />

                    <Row>
                      <Col xs={12} md={6}>
                        <TextField
                          name="fullName"
                          label="Imię i nazwisko"
                          placeholder="np. Anna Kowalska"
                          fullWidth
                          margin="normal"
                          value={values.fullName}
                          onChange={handleChange}
                          error={touched.fullName && Boolean(errors.fullName)}
                          helperText={touched.fullName && errors.fullName}
                          sx={fieldSx}
                        />
                      </Col>

                      <Col xs={12} md={6}>
                        <TextField
                          name="email"
                          type="email"
                          label="Adres e-mail"
                          placeholder="np. anna@email.pl"
                          fullWidth
                          margin="normal"
                          value={values.email}
                          onChange={(e) => {
                            handleChange(e);
                            setEmail(e.target.value);
                          }}
                          error={
                            (touched.email && Boolean(errors.email)) ||
                            showTextEmail
                          }
                          helperText={
                            showTextEmail
                              ? textEmail
                              : touched.email
                                ? errors.email
                                : "Na ten adres będziemy wysyłać informacje dotyczące konta."
                          }
                          sx={fieldSx}
                        />
                      </Col>

                      <Col xs={12} md={6}>
                        <TextField
                          name="phone"
                          label="Numer telefonu"
                          placeholder="123 456 789"
                          fullWidth
                          margin="normal"
                          value={values.phone}
                          onChange={(e) => {
                            const value = e.target.value
                              .replace(/\D/g, "")
                              .slice(0, 9);

                            setPhone(value);
                            setFieldValue("phone", value);
                          }}
                          error={
                            (touched.phone && Boolean(errors.phone)) ||
                            showTextPhone
                          }
                          helperText={
                            showTextPhone
                              ? textPhone
                              : touched.phone
                                ? errors.phone
                                : "Wyślemy na ten numer jednorazowy kod weryfikacyjny."
                          }
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                +48
                              </InputAdornment>
                            ),
                          }}
                          sx={fieldSx}
                        />
                      </Col>

                      <Col xs={12} md={6}>
                        <FormControl
                          fullWidth
                          margin="normal"
                          error={touched.birthyear && Boolean(errors.birthyear)}
                          sx={fieldSx}
                        >
                          <InputLabel>Rok urodzenia</InputLabel>

                          <Select
                            name="birthyear"
                            label="Rok urodzenia"
                            value={values.birthyear}
                            onChange={handleChange}
                            sx={{
                              borderRadius: 2,
                              backgroundColor: "#fff",
                            }}
                          >
                            {[...Array(60)].map((_, i) => {
                              const year = new Date().getFullYear() - 28 - i;

                              return (
                                <MenuItem key={year} value={year}>
                                  {year}
                                </MenuItem>
                              );
                            })}
                          </Select>

                          {touched.birthyear && errors.birthyear && (
                            <FormHelperText>{errors.birthyear}</FormHelperText>
                          )}
                        </FormControl>
                      </Col>

                      <Col xs={12} md={6}>
                        <FormControl
                          fullWidth
                          margin="normal"
                          error={
                            touched.citizenship && Boolean(errors.citizenship)
                          }
                        >
                          <InputLabel>Obywatelstwo</InputLabel>

                          <Select
                            name="citizenship"
                            label="Obywatelstwo"
                            value={values.citizenship}
                            onChange={(e) => {
                              setFieldValue("citizenship", e.target.value);
                            }}
                            sx={{
                              borderRadius: 2,
                              backgroundColor: "#fff",
                            }}
                          >
                            {citizenshipList.length === 0 && (
                              <MenuItem disabled>
                                <CircularProgress size={18} sx={{ mr: 1 }} />
                                Ładowanie...
                              </MenuItem>
                            )}

                            {citizenshipList.map((v) => (
                              <MenuItem key={v.id} value={v.id}>
                                {v.name_pl}
                              </MenuItem>
                            ))}
                          </Select>

                          {touched.citizenship && errors.citizenship && (
                            <FormHelperText>
                              {errors.citizenship}
                            </FormHelperText>
                          )}
                        </FormControl>
                      </Col>
                    </Row>

                    <Divider sx={{ my: 4 }} />

                    {/* =========================================
                        MIEJSCE ZAMIESZKANIA
                    ========================================= */}

                    <SectionTitle
                      icon={<LocationOnOutlinedIcon />}
                      title="Miejsce zamieszkania"
                      description="Dzięki temu rodziny będą mogły znaleźć opiekunki w swojej okolicy."
                    />

                    <Row>
                      <LocationSelect
                        values={values}
                        setFieldValue={setFieldValue}
                        errors={errors}
                        touched={touched}
                      />
                    </Row>

                    <Divider sx={{ my: 4 }} />

                    {/* =========================================
                        HASŁO
                    ========================================= */}

                    <SectionTitle
                      icon={<LockOutlinedIcon />}
                      title="Bezpieczeństwo konta"
                      description="Utwórz bezpieczne hasło, którego będziesz używać podczas logowania."
                    />

                    <Row>
                      <PasswordFields
                        values={values}
                        handleChange={handleChange}
                        errors={errors}
                        touched={touched}
                      />
                    </Row>

                    <Divider sx={{ my: 4 }} />

                    {/* =========================================
                        RODO
                    ========================================= */}

                    <Box>
                      <FormControlLabel
                        sx={{
                          alignItems: "flex-start",
                          m: 0,
                        }}
                        control={
                          <Checkbox
                            name="rodo"
                            checked={values.rodo}
                            onChange={handleChange}
                            color="success"
                            sx={{
                              mt: -0.5,
                            }}
                          />
                        }
                        label={
                          <Typography
                            variant="body2"
                            sx={{
                              lineHeight: 1.6,
                              color: "#555",
                            }}
                          >
                            Akceptuję zasady przetwarzania danych osobowych
                            opisane w{" "}
                            <a
                              href="/polityka-prywatnosci"
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                color: "#2e7d32",
                                fontWeight: 600,
                              }}
                            >
                              Polityce prywatności
                            </a>
                            .
                          </Typography>
                        }
                      />

                      {touched.rodo && errors.rodo && (
                        <FormHelperText error>{errors.rodo}</FormHelperText>
                      )}
                    </Box>

                    {/* =========================================
                        CAPTCHA
                    ========================================= */}

                    <Box
                      sx={{
                        mt: 3,
                        display: "flex",
                        justifyContent: {
                          xs: "flex-start",
                          sm: "center",
                        },
                        overflowX: "auto",
                      }}
                    >
                      <ReCAPTCHA
                        sitekey="6Ld8dZcsAAAAAI2kdXLZVwduM4ydXysrZRJju4qo"
                        onChange={setCaptchaToken}
                      />
                    </Box>

                    {/* =========================================
                        CTA
                    ========================================= */}

                    <Box
                      sx={{
                        mt: 4,
                        maxWidth: 520,
                        mx: "auto",
                      }}
                    >
                      {showSubmit ? (
                        <Button
                          type="submit"
                          variant="contained"
                          fullWidth
                          size="large"
                          disabled={isSubmitting}
                          endIcon={!isSubmitting && <ArrowForwardIcon />}
                          sx={{
                            py: 1.6,
                            borderRadius: 2.5,
                            textTransform: "none",
                            fontWeight: 700,
                            fontSize: "1.05rem",
                            backgroundColor: "#2e7d32",
                            boxShadow: "0 6px 18px rgba(46,125,50,0.22)",

                            "&:hover": {
                              backgroundColor: "#256628",
                              boxShadow: "0 8px 22px rgba(46,125,50,0.30)",
                            },
                          }}
                        >
                          {isSubmitting ? (
                            <>
                              <CircularProgress
                                size={20}
                                color="inherit"
                                sx={{ mr: 1 }}
                              />
                              Tworzenie konta...
                            </>
                          ) : (
                            "Załóż bezpłatne konto"
                          )}
                        </Button>
                      ) : (
                        <Alert severity="warning">
                          Popraw zaznaczone dane, aby kontynuować rejestrację.
                        </Alert>
                      )}

                      <Typography
                        variant="body2"
                        color="text.secondary"
                        align="center"
                        sx={{
                          mt: 1.5,
                          lineHeight: 1.5,
                        }}
                      >
                        W następnym kroku wyślemy SMS z kodem potwierdzającym
                        Twój numer telefonu.
                      </Typography>
                    </Box>

                    {/* =========================================
                        LOGOWANIE
                    ========================================= */}

                    <Box
                      sx={{
                        mt: 4,
                        textAlign: "center",
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Masz już konto?{" "}
                        <Box
                          component="span"
                          onClick={() => navigate("/login")}
                          sx={{
                            color: "#2e7d32",
                            fontWeight: 700,
                            cursor: "pointer",

                            "&:hover": {
                              textDecoration: "underline",
                            },
                          }}
                        >
                          Zaloguj się
                        </Box>
                      </Typography>
                    </Box>
                  </Form>
                )}
              </Formik>
            )}

            {/* =================================================
                KROK 2 - SMS
            ================================================= */}

            {step === 2 && (
              <Box
                sx={{
                  maxWidth: 520,
                  mx: "auto",
                  textAlign: "center",
                }}
              >
                <Box
                  sx={{
                    width: 72,
                    height: 72,
                    borderRadius: "50%",
                    backgroundColor: "#edf7ed",
                    color: "#2e7d32",
                    mx: "auto",
                    mb: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <SmsOutlinedIcon
                    sx={{
                      fontSize: 38,
                    }}
                  />
                </Box>

                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 800,
                    mb: 1,
                  }}
                >
                  Potwierdź numer telefonu
                </Typography>

                <Typography
                  color="text.secondary"
                  sx={{
                    mb: 3,
                    lineHeight: 1.7,
                  }}
                >
                  Wysłaliśmy kod SMS na numer telefonu{" "}
                  <strong>+48 {phone}</strong>. Wpisz otrzymany kod poniżej.
                </Typography>

                <Formik
                  initialValues={{
                    code: "",
                  }}
                  validationSchema={Yup.object({
                    code: Yup.string()
                      .required("Wpisz kod otrzymany SMS-em")
                      .matches(/^[0-9]+$/, "Kod powinien zawierać tylko cyfry"),
                  })}
                  onSubmit={async (values, { setSubmitting }) => {
                    setShowApiResponse(false);

                    try {
                      const response = await apiRequest("verify-sms", "POST", {
                        phone,
                        code: values.code,
                      });

                      if (response.success === true) {
                        setApiResponse({
                          title: "Konto zostało aktywowane",
                          severity: "success",
                          info: "Twoje konto jest już aktywne. Za chwilę przejdziemy do logowania.",
                        });

                        setShowApiResponse(true);
                        setCountdown(5);

                        const timer = setInterval(() => {
                          setCountdown((prev) => prev - 1);
                        }, 1000);

                        setTimeout(() => {
                          clearInterval(timer);
                          navigate("/login");
                        }, 5000);
                      } else {
                        setApiResponse({
                          title: "Nieprawidłowy kod SMS",
                          severity: "warning",
                          info: "Sprawdź kod i spróbuj ponownie.",
                        });

                        setShowApiResponse(true);
                      }
                    } catch (error) {
                      console.error(error);

                      setApiResponse({
                        title: "Nie udało się zweryfikować kodu",
                        severity: "error",
                        info: "Spróbuj ponownie za chwilę.",
                      });

                      setShowApiResponse(true);
                    } finally {
                      setSubmitting(false);
                    }
                  }}
                >
                  {({
                    values,
                    handleChange,
                    errors,
                    touched,
                    isSubmitting,
                  }) => (
                    <Form>
                      <TextField
                        name="code"
                        label="Kod SMS"
                        placeholder="Wpisz kod"
                        fullWidth
                        autoFocus
                        value={values.code}
                        onChange={handleChange}
                        error={touched.code && Boolean(errors.code)}
                        helperText={touched.code && errors.code}
                        inputProps={{
                          inputMode: "numeric",
                          style: {
                            textAlign: "center",
                            fontSize: "1.4rem",
                            letterSpacing: "0.25em",
                          },
                        }}
                        sx={fieldSx}
                      />

                      <Button
                        type="submit"
                        variant="contained"
                        fullWidth
                        size="large"
                        disabled={isSubmitting}
                        sx={{
                          mt: 2,
                          py: 1.5,
                          borderRadius: 2.5,
                          textTransform: "none",
                          fontWeight: 700,
                          backgroundColor: "#2e7d32",

                          "&:hover": {
                            backgroundColor: "#256628",
                          },
                        }}
                      >
                        {isSubmitting
                          ? "Sprawdzanie kodu..."
                          : "Potwierdź i aktywuj konto"}
                      </Button>

                      <Button
                        type="button"
                        variant="text"
                        onClick={() => {
                          setStep(1);
                          setShowApiResponse(false);
                        }}
                        sx={{
                          mt: 1,
                          textTransform: "none",
                          color: "text.secondary",
                        }}
                      >
                        Wróć i popraw dane
                      </Button>
                    </Form>
                  )}
                </Formik>

                {showApiResponse && apiResponse.severity === "success" && (
                  <Box sx={{ mt: 3 }}>
                    <LinearProgress />

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 1 }}
                    >
                      Przejście do logowania za <strong>{countdown}</strong>{" "}
                      sek.
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </Paper>

          {/* ===================================================
              DOLNA INFORMACJA
          =================================================== */}

          <Typography
            variant="body2"
            color="text.secondary"
            align="center"
            sx={{
              mt: 3,
              px: 2,
              lineHeight: 1.6,
            }}
          >
            Dbamy o bezpieczeństwo Twoich danych. Informacje podane podczas
            rejestracji wykorzystujemy wyłącznie do obsługi konta i
            funkcjonowania serwisu TwojaOpiekunka.pl.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}
